import type { AppContext } from "../app/app";
import {
  DOMAIN_CATALOGUE,
  groupCatalogue,
  searchCatalogue,
} from "../data/domains";
import { normaliseTopic, type PersonId, type Topic } from "../data/model";
import { el } from "./dom";

// Per-person domain flags: what a person covers (a SEPARATE axis from the
// qualities, which rank how good they are). Flags show as removable chips; the
// picker is a grouped, searchable type-ahead over the global catalogue, with
// custom additions allowed and deduped on entry. Storage stays flat: only a
// normalised topic key is written, never a group.
export async function renderPersonDomains(
  ctx: AppContext,
  personId: PersonId,
): Promise<HTMLElement> {
  const [topics, links] = await Promise.all([
    ctx.repo.listTopics(),
    ctx.repo.listPersonTopics({ person_id: personId }),
  ]);
  const topicById = new Map(topics.map((t) => [t.id, t]));
  // Normalised keys currently flagged, so the picker hides what is already on.
  const added = new Set<string>();

  const wrap = el("div", { class: "domains" });
  wrap.append(
    el("span", { class: "label", text: "Domains" }),
    el("p", {
      class: "lede domains__note",
      text:
        "What this person covers. Separate from the qualities above: domains " +
        "filter (good at what), qualities rank (how good).",
    }),
  );

  const chips = el("div", { class: "domain-chips" });
  const emptyMsg = el("span", { class: "domains__empty", text: "No domains yet." });

  const addChipFor = (topic: Topic) => {
    emptyMsg.remove();
    const chip = el(
      "span",
      { class: "domain-chip" },
      el("span", { text: topic.name }),
      el("button", {
        class: "domain-chip__x",
        text: "×",
        attrs: { "aria-label": `Remove ${topic.name}` },
        on: {
          click: async () => {
            await ctx.repo.removePersonTopic(personId, topic.id);
            added.delete(topic.key);
            chip.remove();
            if (chips.childElementCount === 0) chips.append(emptyMsg);
            renderResults();
          },
        },
      }),
    );
    chips.append(chip);
  };

  for (const link of links) {
    const t = topicById.get(link.topic_id);
    if (!t) continue;
    added.add(t.key);
    addChipFor(t);
  }
  if (chips.childElementCount === 0) chips.append(emptyMsg);

  const search = el("input", {
    class: "input",
    type: "text",
    placeholder: "Search domains (e.g. credit, wind, EM) or type a custom one",
  });
  const results = el("div", { class: "domain-results" });
  const expanded = new Set<string>();

  const addByName = async (name: string) => {
    const key = normaliseTopic(name);
    if (!key || added.has(key)) return;
    const topic = await ctx.repo.addTopic(name); // dedup by normalised key
    await ctx.repo.addPersonTopic(personId, topic.id);
    added.add(topic.key);
    topicById.set(topic.id, topic);
    addChipFor(topic);
    renderResults(); // keep the search; just drop the now-added entry
  };

  function renderResults(): void {
    results.replaceChildren();
    const q = search.value.trim();
    const searching = q.length > 0;
    const matches = searchCatalogue(q).filter((e) => !added.has(normaliseTopic(e.name)));

    for (const g of groupCatalogue(matches)) {
      const open = searching || expanded.has(g.group);
      const count = g.sections.reduce((n, s) => n + s.entries.length, 0);
      const header = el("button", {
        class: "domain-group__head",
        text: `${open ? "▾" : "▸"} ${g.group} (${count})`,
        on: {
          click: () => {
            if (expanded.has(g.group)) expanded.delete(g.group);
            else expanded.add(g.group);
            renderResults();
          },
        },
      });
      results.append(header);
      if (!open) continue;
      const body = el("div", { class: "domain-group__body" });
      for (const section of g.sections) {
        if (section.subgroup) {
          body.append(el("span", { class: "domain-subgroup", text: section.subgroup }));
        }
        const row = el("div", { class: "domain-options" });
        for (const e of section.entries) {
          row.append(
            el("button", {
              class: "chip domain-option",
              text: e.name,
              on: { click: () => void addByName(e.name) },
            }),
          );
        }
        body.append(row);
      }
      results.append(body);
    }

    // Custom term: offer it when the query is not already an exact catalogue
    // entry or an existing flag.
    const qKey = normaliseTopic(q);
    const isCatalogue = DOMAIN_CATALOGUE.some((e) => normaliseTopic(e.name) === qKey);
    if (searching && qKey && !isCatalogue && !added.has(qKey)) {
      results.append(
        el("button", {
          class: "btn btn--ghost domain-custom",
          text: `Add "${q}" as a custom domain`,
          on: { click: () => void addByName(q) },
        }),
      );
    }
  }

  search.addEventListener("input", () => renderResults());
  search.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key !== "Enter") return;
    const q = search.value.trim();
    const exact = searchCatalogue(q).find((c) => normaliseTopic(c.name) === normaliseTopic(q));
    void addByName(exact ? exact.name : q);
  });
  renderResults();

  wrap.append(chips, el("div", { class: "domain-add" }, search), results);
  return wrap;
}
